// Admin dashboard — hidden from main nav, accessible at /admin.
// Gated server-side by ADMIN_EMAILS env var. Front-end calls
// /api/admin/me to decide whether to render data or a 403 panel.
//
// Two tabs: Waitlist · Conversations. Each renders a paginated table
// with search + an "Export to CSV" button that triggers the streaming
// download endpoint.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Download, Search, ShieldCheck, ShieldAlert, Users, MessageSquare,
  ChevronLeft, ChevronRight, RotateCw,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { api } from "@/lib/api";

const TABS = [
  { key: "waitlist",       label: "Waitlist",       Icon: Users },
  { key: "conversations",  label: "Conversations",  Icon: MessageSquare },
];
const PAGE_SIZE = 50;

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

// API base — every authenticated request must go through the configured
// REACT_APP_BACKEND_URL (proxy strips/forwards /api correctly).
const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const csvUrl = (path) => `${API_BASE}/api${path}`;

export default function AdminDashboard() {
  const [authed, setAuthed] = useState(null); // null=loading, false=denied, true=admin
  const [configured, setConfigured] = useState(true);
  const [tab, setTab] = useState("waitlist");
  const [stats, setStats] = useState(null);

  // Gate
  useEffect(() => {
    let alive = true;
    api.get("/admin/me").then(
      ({ data }) => {
        if (!alive) return;
        setAuthed(!!data?.is_admin);
        setConfigured(data?.configured !== false);
      },
      () => {
        if (alive) setAuthed(false);
      },
    );
    return () => { alive = false; };
  }, []);

  // Top-line stats (once we know we're admin)
  useEffect(() => {
    if (!authed) return;
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, [authed]);

  if (authed === null) {
    return (
      <div className="cartiq-wellness min-h-screen bg-[#F8FAFC] text-[#334155] flex items-center justify-center" data-testid="admin-loading">
        <div className="flex items-center gap-3 text-sm text-[#64748B]">
          <div className="w-2 h-2 rounded-full bg-[#3EB489] animate-pulse" />
          Checking admin access…
        </div>
      </div>
    );
  }
  if (!authed) {
    return (
      <div className="cartiq-wellness min-h-screen bg-[#F8FAFC] text-[#334155] flex items-center justify-center px-4" data-testid="admin-denied">
        <div className="cartiq-card max-w-md text-center">
          <ShieldAlert className="w-10 h-10 mx-auto text-[#FB7185]" />
          <h1 className="font-display uppercase text-2xl text-[#0F172A] mt-3">
            Admin only
          </h1>
          <p className="text-sm text-[#64748B] mt-2">
            {configured
              ? "Your account isn't on the admin allowlist. Reach out to the team if you need access."
              : "Admin allowlist not configured on this environment."}
          </p>
          <a href="/" className="inline-block mt-5 px-5 py-2.5 rounded-lg bg-[#3EB489] hover:bg-[#2D8C6E] text-white text-[12px] font-bold uppercase tracking-[0.14em]">
            Back to home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="cartiq-wellness min-h-screen bg-[#F8FAFC] text-[#334155]" data-testid="admin-dashboard">
      <Toaster position="top-center" richColors theme="light" />
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
        {/* Hero */}
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-6 h-6 text-[#3EB489] mt-1.5" />
          <div className="flex-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#3EB489]">
              Internal · CartIQ 360
            </div>
            <h1 className="font-display uppercase text-3xl md:text-5xl leading-[1.05] text-[#0F172A]" style={{ fontFamily: "'Pirata One', serif" }}>
              Admin Dashboard
            </h1>
            <p className="mt-2 text-sm text-[#64748B] max-w-2xl">
              Investor-grade exports. All data lives in MongoDB; CSV streams
              are real-time, never cached.
            </p>
          </div>
        </div>

        {/* Stats hero */}
        {stats ? (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4" data-testid="admin-stats">
            <StatCard label="Waitlist signups" value={stats.waitlist.total} sub={`${stats.waitlist.last_7d} in last 7d`} testId="stat-waitlist-total" />
            <StatCard label="Conversations" value={stats.conversations.total} sub={`${stats.conversations.last_7d} in last 7d`} testId="stat-conversations-total" />
            <StatCard label="Unique conv. users" value={stats.conversations.unique_users} sub="lifetime" testId="stat-unique-users" />
          </div>
        ) : null}

        {/* Tabs */}
        <div className="mt-10 flex gap-1 p-1 rounded-full bg-[#F1F5F9] w-fit">
          {TABS.map((t) => {
            const active = t.key === tab;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                data-testid={`admin-tab-${t.key}`}
                className={`inline-flex items-center gap-2 py-2 px-4 rounded-full text-[12px] font-bold uppercase tracking-[0.12em] transition-colors ${
                  active
                    ? "bg-[#3EB489] text-white shadow-sm"
                    : "text-[#64748B] hover:text-[#334155]"
                }`}
              >
                <t.Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="mt-6">
          {tab === "waitlist" ? (
            <WaitlistTable />
          ) : (
            <ConversationsTable />
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, testId }) {
  return (
    <div className="cartiq-card !p-5" data-testid={testId}>
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#64748B]">
        {label}
      </div>
      <div className="mt-1 text-4xl font-extrabold font-mono-num text-[#0F172A] tracking-tight">
        {(value ?? 0).toLocaleString("en-IN")}
      </div>
      <div className="mt-1 text-xs text-[#64748B]">{sub}</div>
    </div>
  );
}

function WaitlistTable() {
  return (
    <DataTable
      kind="waitlist"
      endpoint="/admin/waitlist"
      csvEndpoint="/admin/waitlist.csv"
      csvFilename="cartiq_waitlist.csv"
      searchPlaceholder="Search by email, name, source…"
      columns={[
        { key: "created_at", header: "Joined",   render: (r) => formatDate(r.created_at) },
        { key: "email",      header: "Email",    render: (r) => r.email || "—" },
        { key: "name",       header: "Name",     render: (r) => r.name || "—" },
        { key: "source",     header: "Source",   render: (r) => r.source ? <span className="px-2 py-0.5 rounded-full bg-[#3EB489]/10 text-[#2D8C6E] text-[10px] font-bold uppercase tracking-[0.12em]">{r.source}</span> : "—" },
        { key: "user_id",    header: "User",     render: (r) => r.user_id ? <span className="font-mono text-[11px] text-[#64748B]">{r.user_id.slice(0,12)}…</span> : <span className="text-[#94A3B8] italic text-xs">anon</span> },
      ]}
    />
  );
}

function ConversationsTable() {
  return (
    <DataTable
      kind="conversations"
      endpoint="/admin/conversations"
      csvEndpoint="/admin/conversations.csv"
      csvFilename="cartiq_conversations.csv"
      searchPlaceholder="Search Q/A or user_id…"
      columns={[
        { key: "created_at", header: "Asked",    render: (r) => formatDate(r.created_at) },
        { key: "user_id",    header: "User",     render: (r) => <span className="font-mono text-[11px] text-[#64748B]">{(r.user_id || "—").slice(0,14)}…</span> },
        { key: "plan",       header: "Plan",     render: (r) => <span className="px-2 py-0.5 rounded-full bg-[#F1F5F9] text-[#334155] text-[10px] font-bold uppercase tracking-[0.12em]">{r.plan || "—"}</span> },
        { key: "question",   header: "Question", render: (r) => <span className="text-[#334155] text-sm" title={r.question}>{(r.question || "").slice(0, 80)}{(r.question || "").length > 80 ? "…" : ""}</span>, wide: true },
        { key: "answer",     header: "Answer",   render: (r) => <span className="text-[#64748B] text-sm" title={r.answer}>{(r.answer || "").slice(0, 80)}{(r.answer || "").length > 80 ? "…" : ""}</span>, wide: true },
      ]}
    />
  );
}

function DataTable({ kind, endpoint, csvEndpoint, csvFilename, searchPlaceholder, columns }) {
  const [data, setData] = useState({ total: 0, rows: [], offset: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [offset, setOffset] = useState(0);
  const [downloading, setDownloading] = useState(false);

  // Debounce search input — keep the table responsive without hammering
  // mongo on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset offset when search changes
  useEffect(() => { setOffset(0); }, [debounced]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get(endpoint, {
        params: { limit: PAGE_SIZE, offset, search: debounced || undefined },
      });
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load");
      setData({ total: 0, rows: [], offset: 0 });
    } finally {
      setLoading(false);
    }
  }, [endpoint, offset, debounced]);

  useEffect(() => { load(); }, [load]);

  const handleExport = useCallback(async () => {
    setDownloading(true);
    try {
      // Cookie-credentialed fetch so the admin session goes through.
      const url = csvUrl(csvEndpoint);
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) {
        const detail = await res.text();
        throw new Error(detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = csvFilename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
      const count = res.headers.get("X-Row-Count");
      toast.success(`Exported ${count || "?"} rows`);
    } catch (e) {
      toast.error(e?.message || "Export failed");
    } finally {
      setDownloading(false);
    }
  }, [csvEndpoint, csvFilename]);

  const pageStart = data.rows.length ? offset + 1 : 0;
  const pageEnd = offset + data.rows.length;
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < data.total;

  return (
    <div data-testid={`admin-table-${kind}`}>
      {/* Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            data-testid={`admin-search-${kind}`}
            className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white border border-[#E2E8F0] text-sm text-[#334155] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#3EB489] focus:ring-2 focus:ring-[#3EB489]/20"
          />
        </div>
        <div className="flex items-center gap-2 md:ml-auto">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid={`admin-refresh-${kind}`}
            className="inline-flex items-center gap-2 px-3 py-2.5 rounded-lg border border-[#E2E8F0] bg-white text-[#64748B] hover:text-[#334155] hover:border-[#CBD5E1] text-[11px] font-bold uppercase tracking-[0.12em] disabled:opacity-40"
          >
            <RotateCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={downloading || data.total === 0}
            data-testid={`admin-export-${kind}-btn`}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#3EB489] hover:bg-[#2D8C6E] text-white text-[12px] font-bold uppercase tracking-[0.14em] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-3.5 h-3.5" />
            {downloading ? "Exporting…" : "Export to CSV"}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="cartiq-card !p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid={`admin-table-${kind}-data`}>
            <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
              <tr>
                {columns.map((c) => (
                  <th
                    key={c.key}
                    className={`text-left px-4 py-3 text-[10px] font-bold uppercase tracking-[0.14em] text-[#64748B] ${c.wide ? "min-w-[320px]" : ""}`}
                  >
                    {c.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={columns.length} className="px-4 py-12 text-center text-sm text-[#64748B]">Loading…</td></tr>
              ) : data.rows.length === 0 ? (
                <tr><td colSpan={columns.length} className="px-4 py-12 text-center text-sm text-[#64748B] italic">{debounced ? "No matches." : "No data yet."}</td></tr>
              ) : (
                data.rows.map((r) => (
                  <tr key={r.id || `${r.created_at}-${r.email || r.user_id}`} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]/60">
                    {columns.map((c) => (
                      <td key={c.key} className="px-4 py-3 align-top">
                        {c.render(r)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer pagination */}
        {data.total > 0 ? (
          <div className="flex items-center justify-between gap-3 px-4 py-3 bg-[#F8FAFC] border-t border-[#E2E8F0]">
            <div className="text-xs text-[#64748B]" data-testid={`admin-pager-${kind}`}>
              Showing <span className="font-bold text-[#334155]">{pageStart.toLocaleString("en-IN")}</span>–<span className="font-bold text-[#334155]">{pageEnd.toLocaleString("en-IN")}</span> of <span className="font-bold text-[#334155]">{data.total.toLocaleString("en-IN")}</span>
            </div>
            <div className="flex items-center gap-1">
              <button type="button" disabled={!canPrev} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} className="p-1.5 rounded-md hover:bg-white border border-transparent hover:border-[#E2E8F0] text-[#64748B] disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Previous page">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button type="button" disabled={!canNext} onClick={() => setOffset(offset + PAGE_SIZE)} className="p-1.5 rounded-md hover:bg-white border border-transparent hover:border-[#E2E8F0] text-[#64748B] disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Next page">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
